import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const GALLERY_ITEMS = [
  {
    id: '1',
    image: 'https://images.unsplash.com/photo-1695459590088-d6fd3cc97cfa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwc25lYWtlcnN8ZW58MHx8fHwxNzc0NjU2MDQ2fDA&ixlib=rb-4.1.0&q=85',
    category: 'sneakers',
    title: 'Premium Sneaker Restoration',
  },
  {
    id: '2',
    image: 'https://images.unsplash.com/photo-1611688599669-e0d5a0497670?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBsZWF0aGVyJTIwaGFuZGJhZ3xlbnwwfHx8fDE3NzQ2NTYwNDV8MA&ixlib=rb-4.1.0&q=85',
    category: 'handbags',
    title: 'Designer Handbag Care',
  },
  {
    id: '3',
    image: 'https://images.unsplash.com/photo-1598099947145-e85739e7ca28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsZWF0aGVyJTIwaGFuZGJhZ3xlbnwwfHx8fDE3NzQ2NTYwNDV8MA&ixlib=rb-4.1.0&q=85',
    category: 'handbags',
    title: 'Leather Bag Restoration',
  },
  {
    id: '4',
    image: 'https://images.pexels.com/photos/6965875/pexels-photo-6965875.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    category: 'sneakers',
    title: 'Yeezy Cleaning',
  },
  {
    id: '5',
    image: 'https://images.pexels.com/photos/34510831/pexels-photo-34510831.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    category: 'shoes',
    title: 'Leather Shoe Repair',
  },
  {
    id: '6',
    image: 'https://images.unsplash.com/photo-1691480250099-a63081ecfcb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBsZWF0aGVyJTIwaGFuZGJhZ3xlbnwwfHx8fDE3NzQ2NTYwNDV8MA&ixlib=rb-4.1.0&q=85',
    category: 'handbags',
    title: 'Luxury Bag Care',
  },
  {
    id: '7',
    image: 'https://images.unsplash.com/photo-1768550005921-8782adcb798c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxzaG9lJTIwY3JhZnRzbWFufGVufDB8fHx8MTc3NDY1NjA0N3ww&ixlib=rb-4.1.0&q=85',
    category: 'shoes',
    title: 'Expert Craftsmanship',
  },
  {
    id: '8',
    image: 'https://images.unsplash.com/photo-1695459590088-d6fd3cc97cfa?crop=entropy&cs=srgb&fm=jpg',
    category: 'sneakers',
    title: 'Limited Edition Care',
  },
];

const CATEGORIES = ['all', 'shoes', 'sneakers', 'handbags'];

const FadeInSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredItems = activeCategory === 'all'
    ? GALLERY_ITEMS
    : GALLERY_ITEMS.filter(item => item.category === activeCategory);

  return (
    <div data-testid="gallery-page" className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-[#F5F0E6]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
              Our Work
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A1A1A] mb-6">
              Gallery
            </h1>
            <p className="font-body text-base md:text-lg text-[#66635B] max-w-2xl mx-auto">
              Browse through our portfolio of transformed luxury items. Every piece tells a story of meticulous care and expert restoration.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter & Gallery */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Category Filter */}
          <div className="flex justify-center gap-4 mb-12 flex-wrap">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                data-testid={`filter-${category}`}
                className={`font-body text-xs uppercase tracking-[0.15em] px-6 py-3 transition-all ${
                  activeCategory === category
                    ? 'bg-[#1A1A1A] text-white'
                    : 'bg-[#FAFAFA] text-[#66635B] hover:bg-[#EAE5D9]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Gallery Grid - Bento Style */}
          <motion.div
            layout
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          >
            <AnimatePresence mode="popLayout">
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  className={`${
                    index === 0 || index === 5 ? 'md:col-span-2 md:row-span-2' : ''
                  }`}
                >
                  <FadeInSection delay={index * 0.05}>
                    <div
                      data-testid={`gallery-item-${item.id}`}
                      className="group cursor-pointer img-hover relative overflow-hidden aspect-square"
                      onClick={() => setSelectedImage(item)}
                    >
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                        <span className="font-body text-xs uppercase tracking-wider text-[#C5A059]">
                          {item.category}
                        </span>
                        <h3 className="font-heading text-lg text-white">
                          {item.title}
                        </h3>
                      </div>
                    </div>
                  </FadeInSection>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              data-testid="close-lightbox"
              className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X size={32} />
            </button>
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-w-4xl max-h-[80vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.image}
                alt={selectedImage.title}
                className="max-w-full max-h-[80vh] object-contain"
              />
              <div className="mt-4 text-center">
                <span className="font-body text-xs uppercase tracking-wider text-[#C5A059]">
                  {selectedImage.category}
                </span>
                <h3 className="font-heading text-2xl text-white mt-1">
                  {selectedImage.title}
                </h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
